import { FinancialTool } from "../../types";

export const creditAndInvestmentTools: FinancialTool[] = [
  {
    id: "credit-utilization",
    name: "Credit Utilization Calculator",
    description: "Analyze credit card balances against limits to optimize credit utilization.",
    category: "loans",
    isHighCPC: true,
    inputs: [
      { id: "card1Limit", label: "Card 1 Credit Limit", type: "currency", defaultValue: 5000, min: 200, max: 100000, step: 100 },
      { id: "card1Balance", label: "Card 1 Balance", type: "currency", defaultValue: 1500, min: 0, max: 100000, step: 50 },
      { id: "card2Limit", label: "Card 2 Credit Limit", type: "currency", defaultValue: 10000, min: 200, max: 100000, step: 100 },
      { id: "card2Balance", label: "Card 2 Balance", type: "currency", defaultValue: 2000, min: 0, max: 100000, step: 50 },
      { id: "card3Limit", label: "Card 3 Credit Limit", type: "currency", defaultValue: 3000, min: 200, max: 100000, step: 100 },
      { id: "card3Balance", label: "Card 3 Balance", type: "currency", defaultValue: 500, min: 0, max: 100000, step: 50 }
    ],
    calculate: (inputs) => {
      const c1Limit = inputs.card1Limit || 0;
      const c1Bal = inputs.card1Balance || 0;
      const c2Limit = inputs.card2Limit || 0;
      const c2Bal = inputs.card2Balance || 0;
      const c3Limit = inputs.card3Limit || 0;
      const c3Bal = inputs.card3Balance || 0;

      const totalLimit = c1Limit + c2Limit + c3Limit;
      const totalBalance = c1Bal + c2Bal + c3Bal;
      const overallUtilization = totalLimit > 0 ? (totalBalance / totalLimit) * 100 : 0;

      const c1Util = c1Limit > 0 ? (c1Bal / c1Limit) * 100 : 0;
      const c2Util = c2Limit > 0 ? (c2Bal / c2Limit) * 100 : 0;
      const c3Util = c3Limit > 0 ? (c3Bal / c3Limit) * 100 : 0;

      let recommendation = "";
      if (overallUtilization <= 10) {
        recommendation = "Excellent! Your credit utilization is below 10%, which credit scoring models favor heavily. Keep up the amazing work.";
      } else if (overallUtilization <= 30) {
        recommendation = "Good. Your utilization is within the recommended 30% threshold. To further optimize your score, aim to bring it under 10%.";
      } else if (overallUtilization <= 50) {
        recommendation = "Elevated risk. Your utilization exceeds 30%. Paying down balances or securing credit limit increases will help boots your score.";
      } else {
        recommendation = "Critical level. High utilization exceeding 50% severely drags your credit score. Prioritize paying down balances to lower your debt load.";
      }

      return {
        outputs: [
          { label: "Overall Credit Utilization", value: overallUtilization, type: "percentage", highlighted: true },
          { label: "Total Outstanding Balance", value: totalBalance, type: "currency" },
          { label: "Total Available Credit", value: totalLimit, type: "currency" }
        ],
        chartData: [
          { name: "Outstanding Debt", value: totalBalance },
          { name: "Unused Credit Limit", value: Math.max(0, totalLimit - totalBalance) }
        ],
        schedule: [
          { period: 1, payment: c1Bal, principal: c1Limit, interest: c1Util, balance: c1Limit - c1Bal },
          { period: 2, payment: c2Bal, principal: c2Limit, interest: c2Util, balance: c2Limit - c2Bal },
          { period: 3, payment: c3Bal, principal: c3Limit, interest: c3Util, balance: c3Limit - c3Bal }
        ],
        recommendation
      };
    },
    guide: {
      overview: "Evaluates your credit card usage ratios to help protect and optimize your score.",
      howToUse: "Enter credit limits and outstanding balances of your cards, then check recommendations.",
      formulaExplanation: "Total Credit Utilization = (Total Outstanding Balances / Total Combined Limits) * 100",
      faq: [
        { question: "What is an ideal credit utilization?", answer: "Keeping utilization under 10% is optimal, while under 30% is generally acceptable." },
        { question: "Does individual card utilization matter?", answer: "Yes, scoring models evaluate both individual cards and combined total utilization ratios." }
      ],
      seoContent800Words: "Credit utilization is a primary scoring factor that dictates your financial capacity."
    }
  },
  {
    id: "credit-score-improvement",
    name: "Credit Score Improvement Calculator",
    description: "Model key actions to estimate prospective credit score positive recovery patterns.",
    category: "loans",
    isHighCPC: true,
    inputs: [
      { id: "currentScore", label: "Current Credit Score", type: "number", defaultValue: 620, min: 300, max: 850, step: 1 },
      { id: "targetScore", label: "Target Credit Score", type: "number", defaultValue: 750, min: 300, max: 850, step: 1 },
      { id: "missedPayments", label: "Missed Payments (Last 24 Months)", type: "number", defaultValue: 2, min: 0, max: 20, step: 1 },
      { id: "derogatoryMarks", label: "Derogatory Marks / Collections", type: "number", defaultValue: 1, min: 0, max: 10, step: 1 },
      { id: "currentUtilization", label: "Current Credit Utilization (%)", type: "percentage", defaultValue: 45, min: 0, max: 100, step: 1 }
    ],
    calculate: (inputs) => {
      const score = inputs.currentScore || 620;
      const target = inputs.targetScore || 750;
      const missed = inputs.missedPayments || 0;
      const marks = inputs.derogatoryMarks || 0;
      const util = inputs.currentUtilization || 0;

      const gap = Math.max(0, target - score);
      
      let estImprovement = 0;
      if (util > 30) {
        estImprovement += Math.min(60, (util - 10) * 1.5);
      }
      estImprovement += Math.max(0, missed * 20);
      estImprovement += Math.max(0, marks * 40);

      const realGain = Math.min(gap, Math.round(estImprovement));
      const timelineMonths = Math.max(3, Math.round(gap / 8));

      return {
        outputs: [
          { label: "Target Score Gap", value: gap, type: "number", highlighted: true },
          { label: "Estimated Point Growth Potential", value: realGain, type: "number" },
          { label: "Projected Timeline (Months)", value: timelineMonths, type: "number" }
        ],
        chartData: [
          { name: "Current Score", value: score },
          { name: "Target Score", value: target },
          { name: "Estimated Boost", value: realGain }
        ],
        recommendation: `Action Plan: Reduce credit card balances to drop utilization below 30% (ideally 10%). Erase late fees through goodwill letters, and dispute inaccurate marks to reach your ${target} score.`
      };
    },
    guide: {
        overview: "Model actions to optimize FICO scores and remove derogatory records.",
        howToUse: "Enter your score, target, missed payments, collections, and current utilization.",
        formulaExplanation: "Calculates score potential by reducing negative factors across FICO risk pillars.",
        faq: [
          { question: "How long do negative marks stay on reports?", answer: "Late payments and collections stay for 7 years, while bankruptcies persist 7-10 years." },
          { question: "Does paying collections raise score?", answer: "Newer FICO 9 & VantageScore models ignore paid collections, but older legacy systems still count them." }
        ],
        seoContent800Words: "FICO credit scoring uses clear payment history and utilization weights to establish risk profiles."
    }
  },
  {
    id: "credit-card-minimum",
    name: "Credit Card Minimum Payment Calculator",
    description: "Compare minimum credit card payments against accelerated fixed strategies to save thousands.",
    category: "loans",
    isHighCPC: true,
    inputs: [
      { id: "balance", label: "Credit Card Balance", type: "currency", defaultValue: 5000, min: 200, max: 50000, step: 100 },
      { id: "apr", label: "Card Interest APR (%)", type: "percentage", defaultValue: 21.99, min: 5, max: 40, step: 0.1 },
      { id: "minPaymentPercent", label: "Minimum Payment Rate (%)", type: "percentage", defaultValue: 2.5, min: 1.5, max: 10, step: 0.1 },
      { id: "minPaymentFloor", label: "Minimum Floor Amount", type: "currency", defaultValue: 25, min: 10, max: 100, step: 5 }
    ],
    calculate: (inputs) => {
      const bal = inputs.balance || 0;
      const apr = (inputs.apr || 0) / 100;
      const minPercent = (inputs.minPaymentPercent || 0) / 100;
      const minFloor = inputs.minPaymentFloor || 25;

      const monthlyRate = apr / 12;

      // Plan 1: Minimum Payment Only
      let balance1 = bal;
      let totalInterest1 = 0;
      let months1 = 0;
      const schedule1 = [];

      while (balance1 > 0.01 && months1 < 480) {
        months1++;
        const interest = balance1 * monthlyRate;
        let minPay = Math.max(minFloor, balance1 * minPercent + interest);
        if (minPay > balance1 + interest) {
          minPay = balance1 + interest;
        }
        const principal = Math.max(0, minPay - interest);
        balance1 = Math.max(0, balance1 - principal);
        totalInterest1 += interest;

        if (months1 % 12 === 0 || months1 <= 12) {
          schedule1.push({
            period: months1,
            payment: minPay,
            principal,
            interest,
            balance: balance1
          });
        }
      }

      // Plan 2: Fixed Payment Strategy (e.g., Fixed $150 or Floor * 2, whichever is higher)
      const fixedMonthly = Math.max(150, minFloor * 3);
      let balance2 = bal;
      let totalInterest2 = 0;
      let months2 = 0;

      while (balance2 > 0.01 && months2 < 480) {
        months2++;
        const interest = balance2 * monthlyRate;
        let pay = Math.min(fixedMonthly, balance2 + interest);
        if (pay < interest + 10) {
          pay = balance2 + interest; // prevent infinite loop if pay too small
        }
        const principal = Math.max(0, pay - interest);
        balance2 = Math.max(0, balance2 - principal);
        totalInterest2 += interest;
      }

      const interestSaved = Math.max(0, totalInterest1 - totalInterest2);
      const timespanReduced = Math.max(0, months1 - months2);

      return {
        outputs: [
          { label: "Minimum-Only Payoff Time", value: `${months1} Months (${(months1/12).toFixed(1)} years)`, type: "text", highlighted: true },
          { label: "Total Interest Paid (Minimums)", value: totalInterest1, type: "currency" },
          { label: "Interest Saved (Fixed Payment)", value: interestSaved, type: "currency", highlighted: true },
          { label: "Years Shaved Off Repayment", value: (timespanReduced / 12).toFixed(1) + " Years", type: "text" }
        ],
        chartData: [
          { name: "Minimum-Only Interest", value: totalInterest1 },
          { name: "Fixed Payment Interest", value: totalInterest2 },
          { name: "Interest Savings Potential", value: interestSaved }
        ],
        schedule: schedule1,
        recommendation: `By upgrading your payment from a declining minimum to a fixed monthly amount of $${fixedMonthly}, you reduce your repayment period by ${(timespanReduced/12).toFixed(1)} years and save $${interestSaved.toFixed(2)} in total interest.`
      };
    },
    guide: {
      overview: "Visualizes the severe costs of credit cards if you only pay minimum bills.",
      howToUse: "Enter credit card balance, current rate APR, minimum factor, and floor.",
      formulaExplanation: "Calculates interest each month on remaining statement balances, while reducing principal by the remainder of the min payment.",
      faq: [
        { question: "Why are credit card minimums so low?", answer: "Lenders choose minimum values to maximize their cumulative interest profits over decades." },
        { question: "Will paying slightly more speed up payoff?", answer: "Yes, even regular $50 extra payments can eliminate decades of credit card debt." }
      ],
      seoContent800Words: "Credit card contract amortization relies on small repayment ratios that drag debt over huge timelines."
    }
  },
  {
    id: "balance-transfer",
    name: "Balance Transfer Calculator",
    description: "Evaluate saving on high interest debts using introductory 0% APR promotional cards.",
    category: "loans",
    isHighCPC: true,
    inputs: [
      { id: "transferBalance", label: "Balance to Transfer", type: "currency", defaultValue: 10000, min: 500, max: 100000, step: 500 },
      { id: "currentApr", label: "Current Card APR (%)", type: "percentage", defaultValue: 22.99, min: 5, max: 40, step: 0.1 },
      { id: "promotionalMonths", label: "Promo Period (Months)", type: "number", defaultValue: 18, min: 6, max: 36, step: 1 },
      { id: "transferFeePercent", label: "Transfer Fee Rate (%)", type: "percentage", defaultValue: 3, min: 0, max: 10, step: 0.1 },
      { id: "targetMonthlyPayment", label: "Target Monthly Payment", type: "currency", defaultValue: 500, min: 50, max: 5000, step: 25 }
    ],
    calculate: (inputs) => {
      const bal = inputs.transferBalance || 0;
      const currentApr = (inputs.currentApr || 0) / 100;
      const promoMonths = inputs.promotionalMonths || 18;
      const feePercent = (inputs.transferFeePercent || 0) / 100;
      const monthlyPay = inputs.targetMonthlyPayment || 500;

      const transferFee = bal * feePercent;
      const totalNewBalance = bal + transferFee;

      // Plan A: original card
      let balA = bal;
      let originalInterestPaid = 0;
      let monthsA = 0;
      const monthlyRateA = currentApr / 12;

      while (balA > 0.01 && monthsA < 360) {
        monthsA++;
        const interest = balA * monthlyRateA;
        originalInterestPaid += interest;
        const principal = Math.max(0, monthlyPay - interest);
        balA = Math.max(0, balA - principal);
      }

      // Plan B: balance transfer card
      let balB = totalNewBalance;
      let transferInterestPaid = 0;
      let monthsB = 0;

      // promo phase
      for (let m = 1; m <= promoMonths; m++) {
        if (balB <= 0) break;
        monthsB++;
        balB = Math.max(0, balB - monthlyPay);
      }

      // post-promo phase (assumes current APR restarts)
      while (balB > 0.01 && monthsB < 360) {
        monthsB++;
        const interest = balB * monthlyRateA;
        transferInterestPaid += interest;
        const principal = Math.max(0, monthlyPay - interest);
        balB = Math.max(0, balB - principal);
      }

      const totalPromoCost = transferFee + transferInterestPaid;
      const netSavings = Math.max(0, originalInterestPaid - totalPromoCost);

      return {
        outputs: [
          { label: "Net Financial Savings", value: netSavings, type: "currency", highlighted: true },
          { label: "Upfront Balance Transfer Fee", value: transferFee, type: "currency" },
          { label: "Interest Paid on Original Card", value: originalInterestPaid, type: "currency" },
          { label: "Repayment Period (Transfer Card)", value: `${monthsB} Months`, type: "text" }
        ],
        chartData: [
          { name: "Original Card Total Costs", value: originalInterestPaid },
          { name: "Transfer Fee + Post-Promo Costs", value: totalPromoCost },
          { name: "Net Saved Cash", value: netSavings }
        ],
        recommendation: `Transferring saves you an estimated $${netSavings.toFixed(2)} in total interest costs. Make sure to clear the $${totalNewBalance.toFixed(2)} transfer balance inside the ${promoMonths}-month window to secure zero-interest gains.`
      };
    },
    guide: {
      overview: "Evaluates if standard balance transfer products are financially beneficial.",
      howToUse: "Enter target balance, present interest APR, promo months, fee rate, and monthly budget.",
      formulaExplanation: "Total Transfer Bal = Principal * (1 + feeRate). Compares compound interest on old card against fee + interest after promotional period.",
      faq: [
        { question: "What is a common balance transfer fee?", answer: "Most issuers charge between 3% and 5% of the total balance transferred." },
        { question: "Can I transfer debt between same banks?", answer: "No, card companies forbid transferring balances across cards they issue." }
      ],
      seoContent800Words: "Introductions to promotional credit card consolidations require precise timeline tracking to prevent high-interest recapture."
    }
  },
  {
    id: "credit-limit-increase",
    name: "Credit Limit Increase Impact Calculator",
    description: "Determine how a credit limit expansion reduces credit utilization to improve credit worthiness.",
    category: "loans",
    isHighCPC: true,
    inputs: [
      { id: "currentBalance", label: "Current Statement Balance", type: "currency", defaultValue: 3000, min: 0, max: 50000, step: 100 },
      { id: "currentLimit", label: "Existing Credit Limit", type: "currency", defaultValue: 6000, min: 500, max: 100000, step: 500 },
      { id: "requestedIncrease", label: "Limit Increase Amount", type: "currency", defaultValue: 4000, min: 500, max: 100000, step: 500 }
    ],
    calculate: (inputs) => {
      const bal = inputs.currentBalance || 0;
      const oldLimit = inputs.currentLimit || 6000;
      const increase = inputs.requestedIncrease || 0;

      const newLimit = oldLimit + increase;
      const oldUtil = oldLimit > 0 ? (bal / oldLimit) * 100 : 0;
      const newUtil = newLimit > 0 ? (bal / newLimit) * 100 : 0;

      const changeUtil = Math.max(0, oldUtil - newUtil);

      // Score change prediction
      let estimatedImprovement = "Neutral / Tiny Boost (+2 to +10 pts)";
      if (oldUtil > 50 && newUtil <= 30) {
        estimatedImprovement = "High Positive Boost (+30 to +60 pts)";
      } else if (oldUtil > 30 && newUtil <= 10) {
        estimatedImprovement = "Very High Positive Boost (+40 to +75 pts)";
      } else if (oldUtil > 30 && newUtil <= 30) {
        estimatedImprovement = "Moderate Boost (+15 to +30 pts)";
      }

      return {
        outputs: [
          { label: "New Combined Credit Limit", value: newLimit, type: "currency", highlighted: true },
          { label: "Current Credit Utilization Ratio", value: oldUtil, type: "percentage" },
          { label: "Optimized Credit Utilization Ratio", value: newUtil, type: "percentage", highlighted: true },
          { label: "Credit Score Score Impact", value: estimatedImprovement, type: "text" }
        ],
        chartData: [
          { name: "Current Limit", value: oldLimit },
          { name: "New Optimized Limit", value: newLimit },
          { name: "Usage Amount", value: bal }
        ],
        recommendation: `Expanding credit limits by $${increase} lowers utilization from ${oldUtil.toFixed(1)}% to ${newUtil.toFixed(1)}%. This reduces your credit risk rating without forcing you to alter monthly habits.`
      };
    },
    guide: {
      overview: "Models utilization improvements expected after checking credit limit upgrades.",
      howToUse: "Enter current balances, existing credit line limits, and the requested limit increase.",
      formulaExplanation: "Calculates the difference in utilization percentage: (Current Balance / Current Limit) - (Current Balance / New Limit).",
      faq: [
        { question: "Does requesting a limit increase hurt credit score?", answer: "Only if the card provider runs a hard credit inquiry. Many lenders now utilize soft pulls for limits." },
        { question: "How often can I request credit line raises?", answer: "Most credit card institutions allow requests once every 6 to 12 months." }
      ],
      seoContent800Words: "Broad bank approvals on limits decrease revolving ratios, optimizing total credit profiles."
    }
  },
  {
    id: "cagr",
    name: "CAGR Calculator",
    description: "Determine the Compound Annual Growth Rate of your investment over time.",
    category: "savings",
    isHighCPC: true,
    inputs: [
      { id: "initialValue", label: "Initial Investment Value", type: "currency", defaultValue: 10000, min: 100, max: 10000000, step: 500 },
      { id: "finalValue", label: "Final Portfolio Value", type: "currency", defaultValue: 25000, min: 100, max: 10000000, step: 500 },
      { id: "years", label: "Duration (Years)", type: "number", defaultValue: 5, min: 1, max: 50, step: 1 }
    ],
    calculate: (inputs) => {
      const initial = inputs.initialValue || 10000;
      const finalVal = inputs.finalValue || 25000;
      const yrs = inputs.years || 1;

      const cagr = yrs > 0 ? (Math.pow(finalVal / initial, 1 / yrs) - 1) * 100 : 0;
      const totalReturnPercent = ((finalVal - initial) / initial) * 100;
      const absoluteGains = finalVal - initial;

      const chartData = [];
      const rate = cagr / 100;
      for (let y = 0; y <= yrs; y++) {
        chartData.push({
          name: `Year ${y}`,
          value: Math.round(initial * Math.pow(1 + rate, y))
        });
      }

      return {
        outputs: [
          { label: "Compound Annual Growth Rate (CAGR)", value: cagr, type: "percentage", highlighted: true },
          { label: "Total Asset Absolute Returns", value: absoluteGains, type: "currency" },
          { label: "Cumulative Investment ROI (%)", value: totalReturnPercent, type: "percentage" }
        ],
        chartData,
        recommendation: `Your investment generated an annualized growth coefficient (CAGR) of ${cagr.toFixed(2)}%, multiplying initial assets by a factor of ${(finalVal/initial).toFixed(2)}x.`
      };
    },
    guide: {
      overview: "Computes the exact geometric mean representing investment growth annually.",
      howToUse: "Key in standard starting investment values, ending financial valuations, and elapsed years.",
      formulaExplanation: "CAGR = [ (Ending Value / Starting Value) ^ (1 / Years) ] - 1",
      faq: [
        { question: "What is the difference between CAGR and simple annual average?", answer: "CAGR tracks compounding effects, making it a highly precise index metric for volatile markets." },
        { question: "What is a good historical equity CAGR?", answer: "S&P 500 returns represent a historical long-term CAGR of roughly 7% to 10% after inflation." }
      ],
      seoContent800Words: "Annual compound returns normalize volatile yearly market fluctuations into a stable metric."
    }
  },
  {
    id: "portfolio-rebalancing",
    name: "Portfolio Rebalancing Calculator",
    description: "Align your assets to target allocations and identify necessary asset trade commands.",
    category: "savings",
    isHighCPC: true,
    inputs: [
      { id: "asset1Current", label: "Asset A Current Value", type: "currency", defaultValue: 5000, min: 0, max: 1000000, step: 100 },
      { id: "asset1Target", label: "Asset A Target Allocation (%)", type: "percentage", defaultValue: 50, min: 0, max: 100, step: 1 },
      { id: "asset2Current", label: "Asset B Current Value", type: "currency", defaultValue: 3000, min: 0, max: 1000000, step: 100 },
      { id: "asset2Target", label: "Asset B Target Allocation (%)", type: "percentage", defaultValue: 30, min: 0, max: 100, step: 1 },
      { id: "asset3Current", label: "Asset C Current Value", type: "currency", defaultValue: 2000, min: 0, max: 1000000, step: 100 },
      { id: "asset3Target", label: "Asset C Target Allocation (%)", type: "percentage", defaultValue: 20, min: 0, max: 100, step: 1 }
    ],
    calculate: (inputs) => {
      const v1 = inputs.asset1Current || 0;
      const t1 = inputs.asset1Target || 0;
      const v2 = inputs.asset2Current || 0;
      const t2 = inputs.asset2Target || 0;
      const v3 = inputs.asset3Current || 0;
      const t3 = inputs.asset3Target || 0;

      const totalValue = v1 + v2 + v3;
      const sumTarget = t1 + t2 + t3;

      // Adjust targets if they do not sum to 100
      const normT1 = sumTarget > 0 ? (t1 / sumTarget) * 100 : 0;
      const normT2 = sumTarget > 0 ? (t2 / sumTarget) * 100 : 0;
      const normT3 = sumTarget > 0 ? (t3 / sumTarget) * 100 : 0;

      const destV1 = totalValue * (normT1 / 100);
      const destV2 = totalValue * (normT2 / 100);
      const destV3 = totalValue * (normT3 / 100);

      const diff1 = destV1 - v1;
      const diff2 = destV2 - v2;
      const diff3 = destV3 - v3;

      return {
        outputs: [
          { label: "Total Portfolio Balances", value: totalValue, type: "currency", highlighted: true },
          { label: "Asset A Action (Buy if +, Sell if -)", value: diff1, type: "currency" },
          { label: "Asset B Action (Buy if +, Sell if -)", value: diff2, type: "currency" },
          { label: "Asset C Action (Buy if +, Sell if -)", value: diff3, type: "currency" }
        ],
        chartData: [
          { name: "Asset A Current", value: v1 },
          { name: "Asset A Target", value: destV1 },
          { name: "Asset B Current", value: v2 },
          { name: "Asset B Target", value: destV2 },
          { name: "Asset C Current", value: v3 },
          { name: "Asset C Target", value: destV3 }
        ],
        recommendation: `Portfolio rebalancing keeps your risk profiles safe. Action plan: Trade assets to match target allocations: Asset A: $${diff1 > 0 ? "BUY" : "SELL"} ${Math.abs(diff1).toFixed(2)}, Asset B: $${diff2 > 0 ? "BUY" : "SELL"} ${Math.abs(diff2).toFixed(2)}, Asset C: $${diff3 > 0 ? "BUY" : "SELL"} ${Math.abs(diff3).toFixed(2)}.`
      };
    },
    guide: {
      overview: "Identifies asset adjustments required to maintain targeted portfolio weights.",
      howToUse: "Enter present market values and target allocation percentages for three active asset buckets.",
      formulaExplanation: "Calculates total asset sum. Action = (Total Portfolio Value * (Target Allocation % / 100)) - Current Asset Value.",
      faq: [
        { question: "How often should I rebalance my portfolio?", answer: "Most financial advisors recommend rebalancing semi-annually, annually, or when weights shift by more than 5%." },
        { question: "What is a drift in asset allocation?", answer: "Drift is the organic change in portfolio asset weights caused by varying market performance." }
      ],
      seoContent800Words: "Systematic reallocation rules restore risk profiles, ensuring consistent long-term results."
    }
  },
  {
    id: "expense-ratio",
    name: "Expense Ratio Impact Calculator",
    description: "Determine how investment fund expense ratios bleed your overall returns over the years.",
    category: "savings",
    isHighCPC: true,
    inputs: [
      { id: "initialInvestment", label: "Initial Investment Value", type: "currency", defaultValue: 10000, min: 100, max: 2000000, step: 500 },
      { id: "annualContribution", label: "Yearly Contribution Cash", type: "currency", defaultValue: 5000, min: 0, max: 200000, step: 500 },
      { id: "expectedReturn", label: "Annual Rate of Return (%)", type: "percentage", defaultValue: 8, min: 1, max: 30, step: 0.1 },
      { id: "expenseRatio", label: "Fund Expense Ratio (%)", type: "percentage", defaultValue: 1.2, min: 0, max: 5, step: 0.01 },
      { id: "years", label: "Timeline (Years)", type: "number", defaultValue: 30, min: 1, max: 50, step: 1 }
    ],
    calculate: (inputs) => {
      const init = inputs.initialInvestment || 10000;
      const annual = inputs.annualContribution || 5000;
      const grossReturn = (inputs.expectedReturn || 0) / 100;
      const fee = (inputs.expenseRatio || 0) / 100;
      const yrs = inputs.years || 30;

      const netReturn = Math.max(0, grossReturn - fee);

      let totalA = init; // No Fee
      let totalB = init; // With Fee
      const chartData = [];

      for (let y = 1; y <= yrs; y++) {
        totalA = (totalA + annual) * (1 + grossReturn);
        totalB = (totalB + annual) * (1 + netReturn);

        if (y % 5 === 0 || y === yrs) {
          chartData.push({
            name: `Yr ${y}`,
            "No-Fee Value": Math.round(totalA),
            "With-Fee Value": Math.round(totalB),
            "Wealth Lost to Fees": Math.max(0, Math.round(totalA - totalB))
          });
        }
      }

      const totalFeesLost = Math.max(0, totalA - totalB);
      const feeErosionRatio = totalA > 0 ? (totalFeesLost / totalA) * 100 : 0;

      return {
        outputs: [
          { label: "Lost Return Wealth to Fees", value: totalFeesLost, type: "currency", highlighted: true },
          { label: "Portfolio Value (No Fees)", value: totalA, type: "currency" },
          { label: "Portfolio Value (With Fees)", value: totalB, type: "currency", highlighted: true },
          { label: "Fee Erosion Ratio (%)", value: feeErosionRatio, type: "percentage" }
        ],
        chartData,
        recommendation: `A seemingly small expense ratio of ${ (fee*100).toFixed(2) }% consumes a shocking ${feeErosionRatio.toFixed(1)}% of your potential retirement wealth over ${yrs} years, draining $${totalFeesLost.toFixed(2)} in lost interest.`
      };
    },
    guide: {
      overview: "Visualizes how internal mutual fund fees erode compounding growth trajectories.",
      howToUse: "Enter principal, annual deposits, estimated yearly gains, premium expense ratio, and timelines.",
      formulaExplanation: "Calculates future returns side-by-side: Gross Compound Rate vs. Net Rate (Return - Expense Ratio %).",
      faq: [
        { question: "What is a normal index fund expense ratio?", answer: "Broad US index funds feature ratios between 0.03% and 0.15%, while actively managed accounts exceed 1.0%." },
        { question: "How does my brokerage bill expense ratios?", answer: "Brokers do not send invoice bills. They deduct fees daily from the fund's net asset value." }
      ],
      seoContent800Words: "Small structural basis fees deplete huge compounding balances over long investment timelines."
    }
  },
  {
    id: "asset-allocation",
    name: "Asset Allocation Calculator",
    description: "Determine target model allocations based on age, risk tolerance, and retirement plans.",
    category: "savings",
    isHighCPC: true,
    inputs: [
      { id: "investorAge", label: "Your Present Age", type: "number", defaultValue: 30, min: 18, max: 100, step: 1 },
      { id: "retirementAge", label: "Target Retirement Age", type: "number", defaultValue: 65, min: 30, max: 100, step: 1 },
      { id: "riskProfile", label: "Risk Tolerance Level", type: "select", defaultValue: 2, options: [
        { label: "Conservative (Low Risk)", value: 1 },
        { label: "Moderate (Medium Risk)", value: 2 },
        { label: "Aggressive (High Risk)", value: 3 }
      ]}
    ],
    calculate: (inputs) => {
      const age = inputs.investorAge || 30;
      const retAge = inputs.retirementAge || 65;
      const risk = inputs.riskProfile || 2;

      let equityShare = 110 - age;
      if (risk === 1) {
        equityShare = Math.max(20, (100 - age) * 0.7);
      } else if (risk === 3) {
        equityShare = Math.min(95, (120 - age) * 1.15);
      }

      equityShare = Math.max(10, Math.min(95, Math.round(equityShare)));
      const fixedShare = Math.max(5, 100 - equityShare - (risk === 2 ? 5 : 0));
      const cashShare = Math.max(0, 100 - equityShare - fixedShare);

      return {
        outputs: [
          { label: "Suggested Stock Target (%)", value: equityShare, type: "percentage", highlighted: true },
          { label: "Suggested Bond Target (%)", value: fixedShare, type: "percentage" },
          { label: "Suggested Liquid cash Target (%)", value: cashShare, type: "percentage" }
        ],
        chartData: [
          { name: "Stocks", value: equityShare },
          { name: "Bonds", value: fixedShare },
          { name: "Cash Reserves", value: cashShare }
        ],
        recommendation: `Based on your age of ${age} and Moderate-to-Aggressive investing preferences, we recommend a ${equityShare}% Equity structure to drive long-term compound appreciation.`
      };
    },
    guide: {
      overview: "Generates custom asset allocation breakdowns to optimize portfolio risk-return metrics.",
      howToUse: "Input age, target retirement age, and select risk appetite to find your profile allocation.",
      formulaExplanation: "Adjusts classical rule-of-thumb models ('110 minus age') with risk multiplier factors.",
      faq: [
        { question: "What is asset allocation?", answer: "Distributing your visual investment assets across categories like stocks, bonds, and cash." },
        { question: "Why does target allocation shift with retirement timelines?", answer: "Investors reduce volatility as retirement nears by moving capital from stocks back to fixed bonds." }
      ],
      seoContent800Words: "Diversifying assets bounds maximum drawdowns and secures portfolios during stock market cycles."
    }
  },
  {
    id: "dollar-cost-averaging",
    name: "Dollar Cost Averaging Calculator",
    description: "Compare regular periodic small investments with single upfront investment profiles.",
    category: "savings",
    isHighCPC: true,
    inputs: [
      { id: "monthlyDca", label: "Monthly Deposit amount ($)", type: "currency", defaultValue: 500, min: 10, max: 50000, step: 25 },
      { id: "expectedGrowth", label: "Expected Annual Gain (%)", type: "percentage", defaultValue: 9, min: 1, max: 30, step: 0.1 },
      { id: "termYears", label: "Timeline Term (Years)", type: "number", defaultValue: 15, min: 1, max: 40, step: 1 },
      { id: "lumpSumAmount", label: "Initial Single Lump Sum ($)", type: "currency", defaultValue: 15000, min: 100, max: 1000000, step: 500 }
    ],
    calculate: (inputs) => {
      const dca = inputs.monthlyDca || 500;
      const rate = (inputs.expectedGrowth || 0) / 100;
      const yrs = inputs.termYears || 15;
      const lump = inputs.lumpSumAmount || 15000;

      const months = yrs * 12;
      const monthlyRate = rate / 12;

      // DCA Growth
      let dcaBal = 0;
      let totalDcaInvested = 0;
      const chartData = [];

      for (let m = 1; m <= months; m++) {
        dcaBal = (dcaBal + dca) * (1 + monthlyRate);
        totalDcaInvested += dca;

        if (m % 24 === 0 || m === months) {
          chartData.push({
            name: `Yr ${Math.round(m/12)}`,
            "DCA Balance": Math.round(dcaBal),
            "DCA Total Principal": totalDcaInvested,
            "Lump-Sum Balance": Math.round(lump * Math.pow(1 + rate, m / 12))
          });
        }
      }

      const lumpFinal = lump * Math.pow(1 + rate, yrs);

      return {
        outputs: [
          { label: "DCA Portfolio Future Value", value: dcaBal, type: "currency", highlighted: true },
          { label: "Total DCA Out-of-Pocket Cost", value: totalDcaInvested, type: "currency" },
          { label: "Alternative Lump-Sum End Value", value: lumpFinal, type: "currency" },
          { label: "DCA Total Compounded Earnings", value: Math.max(0, dcaBal - totalDcaInvested), type: "currency" }
        ],
        chartData,
        recommendation: `Consistent Dollar Cost Averaging compounds robust returns. Your $${dca}/month savings turn into $${dcaBal.toFixed(2)} total over ${yrs} years, capturing a clean return margin.`
      };
    },
    guide: {
      overview: "Contrast the wealth building outcomes of DCA plans with a lump sum investment strategy.",
      howToUse: "Key in monthly payments, custom investment growth caps, years, and your single investment choice.",
      formulaExplanation: "Calculates monthly compound growth recursively, comparing to standard lump sum compound formulations.",
      faq: [
        { question: "Is DCA safer than investing lump sums?", answer: "Yes, DCA lowers timing risk and helps prevent buying at market peaks." },
        { question: "How does DCA lower average stock costs?", answer: "Because fixed dollar deposits automatically purchase more shares when prices drop, and fewer when prices rise." }
      ],
      seoContent800Words: "Systematic monthly buying structures smooth market entries and eliminate psychological reservation biases."
    }
  },
  {
    id: "index-fund",
    name: "Index Fund Calculator",
    description: "Forecast index fund growth with standard fee tracking structures and dividends.",
    category: "savings",
    isHighCPC: true,
    inputs: [
      { id: "startingCapital", label: "Initial Portfolio Value", type: "currency", defaultValue: 5000, min: 100, max: 2000000, step: 500 },
      { id: "monthlyDeposit", label: "Monthly Account Deposit", type: "currency", defaultValue: 300, min: 0, max: 50000, step: 50 },
      { id: "indexGrowth", label: "Index Core Apprec. Rate (%)", type: "percentage", defaultValue: 8.5, min: 1, max: 25, step: 0.1 },
      { id: "dividendYield", label: "Annual Dividend Yield (%)", type: "percentage", defaultValue: 1.5, min: 0, max: 10, step: 0.1 },
      { id: "trackingError", label: "Annual Fund Expense ratio (%)", type: "percentage", defaultValue: 0.05, min: 0, max: 2, step: 0.01 },
      { id: "years", label: "Years to Compound", type: "number", defaultValue: 20, min: 1, max: 50, step: 1 }
    ],
    calculate: (inputs) => {
      const init = inputs.startingCapital || 5000;
      const deposit = inputs.monthlyDeposit || 300;
      const growth = (inputs.indexGrowth || 0) / 100;
      const divYield = (inputs.dividendYield || 0) / 100;
      const error = (inputs.trackingError || 0) / 100;
      const yrs = inputs.years || 20;

      const netAnnualRate = growth + divYield - error;
      const monthlyRate = netAnnualRate / 12;

      let bal = init;
      let totalDeposited = init;
      const chartData = [];

      for (let m = 1; m <= yrs * 12; m++) {
        bal = (bal + deposit) * (1 + monthlyRate);
        totalDeposited += deposit;

        if (m % 24 === 0 || m === yrs * 12) {
          chartData.push({
            name: `Yr ${Math.round(m/12)}`,
            "Fund Balance": Math.round(bal),
            "Total Invested": totalDeposited
          });
        }
      }

      return {
        outputs: [
          { label: "Projected Index Fund Balance", value: bal, type: "currency", highlighted: true },
          { label: "Total Contributions Made", value: totalDeposited, type: "currency" },
          { label: "Cumulative Compound Gains", value: Math.max(0, bal - totalDeposited), type: "currency", highlighted: true },
          { label: "Net Growth Coefficient (Annual)", value: netAnnualRate * 100, type: "percentage" }
        ],
        chartData,
        recommendation: `Passive index tracking yielding ${ (netAnnualRate*100).toFixed(2) }% net returns converts consistent savings into $${bal.toFixed(2)} total over ${yrs} years.`
      };
    },
    guide: {
      overview: "Projects historical index market growth with real-life dividend and tracking expense modifiers.",
      howToUse: "Enter initial setup funds, monthly contribution limits, asset growth targets, and index dividend yields.",
      formulaExplanation: "Total Return Rate = Capital Growth + Dividend Yield - Mutual Fund Expense Ratio.",
      faq: [
        { question: "What is index fund tracking error?", answer: "The minor performance difference between a passive ETF fund and its target index benchmarks." },
        { question: "Is dividend reinvestment critical?", answer: "Yes, dividend reinvesting drives nearly 40% of standard long-term index compounding returns." }
      ],
      seoContent800Words: "Broad market index trackers avoid costly single-stock risks, driving reliable compound gains."
    }
  },
  {
    id: "brokerage-fee",
    name: "Brokerage Fee Calculator",
    description: "Determine how brokerage commissions and purchase transaction fees impact long term portfolio performance.",
    category: "savings",
    isHighCPC: true,
    inputs: [
      { id: "tradeAmount", label: "Average Trade Order Size", type: "currency", defaultValue: 2000, min: 50, max: 100000, step: 100 },
      { id: "tradeFrequency", label: "Monthly Trade Count", type: "number", defaultValue: 4, min: 1, max: 100, step: 1 },
      { id: "flatCommission", label: "Flat Order Comm. Fee ($)", type: "currency", defaultValue: 4.95, min: 0, max: 100, step: 0.05 },
      { id: "percentageFee", label: "Percentage Trade Fee (%)", type: "percentage", defaultValue: 0.1, min: 0, max: 5, step: 0.05 },
      { id: "annualPlatformFee", label: "Yearly Account Support Fee", type: "currency", defaultValue: 50, min: 0, max: 1000, step: 10 }
    ],
    calculate: (inputs) => {
      const tradeSize = inputs.tradeAmount || 2000;
      const freq = inputs.tradeFrequency || 4;
      const flat = inputs.flatCommission || 0;
      const pct = (inputs.percentageFee || 0) / 100;
      const platformFee = inputs.annualPlatformFee || 0;

      const annualTrades = freq * 12;
      const annualFlatFees = flat * annualTrades;
      const annualPctFees = tradeSize * pct * annualTrades;
      const annualTotalCost = annualFlatFees + annualPctFees + platformFee;

      // Project fee cost + compound cost over 15 years assuming 8% growth opportunity cost
      const years = 15;
      const optReturn = 0.08;
      let totalFeesOnly = 0;
      let compoundedOpportunityCost = 0;
      const chartData = [];

      for (let y = 1; y <= years; y++) {
        totalFeesOnly += annualTotalCost;
        compoundedOpportunityCost = (compoundedOpportunityCost + annualTotalCost) * (1 + optReturn);

        if (y % 3 === 0 || y === years) {
          chartData.push({
            name: `Yr ${y}`,
            "Direct Fees Paid": Math.round(totalFeesOnly),
            "compounded Opportunity Cost": Math.round(compoundedOpportunityCost)
          });
        }
      }

      return {
        outputs: [
          { label: "Annual Transaction Costs", value: annualTotalCost, type: "currency", highlighted: true },
          { label: "Total Direct fees (15 Yrs)", value: totalFeesOnly, type: "currency" },
          { label: "Compounded Wealth Lost (15 Yrs)", value: compoundedOpportunityCost, type: "currency", highlighted: true }
        ],
        chartData,
        recommendation: `Your active trading pattern adds $${annualTotalCost.toFixed(2)} in annual friction. Over 15 years, this results in $${compoundedOpportunityCost.toFixed(2)} in lost retirement wealth after compound returns.`
      };
    },
    guide: {
      overview: "Evaluates the total friction cost of transactional share commissions and yearly fees.",
      howToUse: "Key in trade dimensions, trading volume frequency, broker fee factors, and yearly system upkeep fees.",
      formulaExplanation: "Total Costs = (Flat Commission + (Trade size * percentFee)) * Trades per Year + Platform Fee.",
      faq: [
        { question: "How can I avoid brokerage commissions?", answer: "Switch to zero-commission discount brokers who offer free standard electronic trades." },
        { question: "What are hidden payment-for-order-flow fees?", answer: "Brokers route trades to market makers for small kickbacks, sometimes resulting in slightly worse trade fill prices." }
      ],
      seoContent800Words: "Frequent portfolio transaction adjustments bleed compound capital into third-party financial fees."
    }
  },
  {
    id: "investment-inflation",
    name: "Investment Inflation Calculator",
    description: "Adjust future nominal return targets against inflation to see true purchasing power.",
    category: "savings",
    isHighCPC: true,
    inputs: [
      { id: "initialCapital", label: "Starting Portfolio Cash", type: "currency", defaultValue: 50000, min: 100, max: 5000000, step: 1000 },
      { id: "nominalReturn", label: "Expected Nominal Gain (%)", type: "percentage", defaultValue: 9, min: 1, max: 25, step: 0.1 },
      { id: "inflationRate", label: "Expected Inflation Rate (%)", type: "percentage", defaultValue: 2.5, min: 0, max: 15, step: 0.1 },
      { id: "years", label: "Duration (Years)", type: "number", defaultValue: 20, min: 1, max: 50, step: 1 }
    ],
    calculate: (inputs) => {
      const init = inputs.initialCapital || 50000;
      const nominal = (inputs.nominalReturn || 0) / 100;
      const inflation = (inputs.inflationRate || 0) / 100;
      const yrs = inputs.years || 20;

      const realRate = nominal - inflation;

      const nominalFinal = init * Math.pow(1 + nominal, yrs);
      const realFinal = init * Math.pow(1 + realRate, yrs);
      const purchasingPowerLoss = Math.max(0, nominalFinal - realFinal);

      const chartData = [];
      for (let y = 1; y <= yrs; y++) {
        if (y % 5 === 0 || y === yrs) {
          chartData.push({
            name: `Yr ${y}`,
            "Nominal Value": Math.round(init * Math.pow(1 + nominal, y)),
            "Real Purchasing Power": Math.round(init * Math.pow(1 + realRate, y))
          });
        }
      }

      return {
        outputs: [
          { label: "Inflation-Adjusted Real Value", value: realFinal, type: "currency", highlighted: true },
          { label: "Nominal Future Balance", value: nominalFinal, type: "currency" },
          { label: "Purchasing Power Lost to Inflation", value: purchasingPowerLoss, type: "currency", highlighted: true },
          { label: "Real Rate of Return (Annual)", value: realRate * 100, type: "percentage" }
        ],
        chartData,
        recommendation: `An index reaching $${nominalFinal.toFixed(0)} will carry the purchasing equivalent of $${realFinal.toFixed(0)} today due to ${ (inflation*100).toFixed(1) }% yearly inflation.`
      };
    },
    guide: {
      overview: "Converts nominal expected investment results to real-world future buying power.",
      howToUse: "Input starting portfolio capital, estimated pre-adjusted nominal growth yield, inflation, and years.",
      formulaExplanation: "Real Adjusted Value = Principal * (1 + (Nominal % - Inflation %)) ^ Years.",
      faq: [
        { question: "Why is inflation-adjusted return critical?", answer: "Nominal values look massive, but inflation raises future living expenses, meaning each dollar buys less." },
        { question: "What is standard target inflation?", answer: "Historically, the Federal Reserve seeks to stabilize average long-term US inflation at roughly 2.0%." }
      ],
      seoContent800Words: "Long compounding horizons require adjusting paper returns to preserve future purchasing power."
    }
  },
  {
    id: "real-return",
    name: "Real Return Calculator",
    description: "Deduct capital gains taxes and inflation coefficients to reveal true net profit.",
    category: "savings",
    isHighCPC: true,
    inputs: [
      { id: "initialInvestment", label: "Initial Investment Capital", type: "currency", defaultValue: 100000, min: 1000, max: 10000000, step: 1000 },
      { id: "grossProfit", label: "Gross Annual Return ($)", type: "currency", defaultValue: 12000, min: 10, max: 1000000, step: 100 },
      { id: "taxRate", label: "Capital Gains Tax Rate (%)", type: "percentage", defaultValue: 15, min: 0, max: 50, step: 0.1 },
      { id: "inflation", label: "Average Yearly Inflation (%)", type: "percentage", defaultValue: 2.5, min: 0, max: 15, step: 0.1 }
    ],
    calculate: (inputs) => {
      const init = inputs.initialInvestment || 100000;
      const profit = inputs.grossProfit || 12000;
      const tax = (inputs.taxRate || 0) / 100;
      const inflation = (inputs.inflation || 0) / 100;

      const grossYield = (profit / init) * 100;
      const taxExpense = profit * tax;
      const netProfitAfterTax = profit - taxExpense;

      const postTaxYield = (netProfitAfterTax / init) * 100;
      const realYield = postTaxYield - (inflation * 100);
      const realNetProfit = init * (realYield / 100);

      return {
        outputs: [
          { label: "Real Adjusted Gain (%)", value: realYield, type: "percentage", highlighted: true },
          { label: "Nominal Gross Yield (%)", value: grossYield, type: "percentage" },
          { label: "Capital Gains Tax Paid", value: taxExpense, type: "currency" },
          { label: "Real Profit After Tax & Inflation", value: realNetProfit, type: "currency", highlighted: true }
        ],
        chartData: [
          { name: "Gross Profit", value: profit },
          { name: "Tax Deductions", value: taxExpense },
          { name: "Net Real Value", value: Math.max(0, realNetProfit) }
        ],
        recommendation: `Your pre-tax yield of ${grossYield.toFixed(1)}% drops to a real return of ${realYield.toFixed(2)}% once you pay $${taxExpense.toFixed(2)} in capital gains taxes and adjust for inflation.`
      };
    },
    guide: {
      overview: "Calculates the real purchasing power of investments after taxes and inflation.",
      howToUse: "Enter initial capital balance, expected gross returns, tax brackets, and average inflation values.",
      formulaExplanation: "Real Rate = ((Gross Return * (1 - taxRate)) / Capital) - Inflation.",
      faq: [
        { question: "How can I avoid capital gains tax?", answer: "Invest through tax-advantaged accounts like Roth IRAs or 401(k) plans to shield returns." },
        { question: "What is net real yield?", answer: "The interest rate return representing actual purchasing growth after accounting for taxes and inflation." }
      ],
      seoContent800Words: "Taxes and inflation are the two largest wealth destroyers for long-term investors."
    }
  },
  {
    id: "drip-calculator",
    name: "DRIP Calculator",
    description: "Simulate reinvested dividends to compare compounding versus cash-out plans.",
    category: "savings",
    isHighCPC: true,
    inputs: [
      { id: "initialShares", label: "Initial Share Count", type: "number", defaultValue: 500, min: 1, max: 1000000, step: 10 },
      { id: "sharePrice", label: "Initial Share Price ($)", type: "currency", defaultValue: 50, min: 1, max: 10000, step: 1 },
      { id: "dividendYield", label: "Annual Dividend Yield (%)", type: "percentage", defaultValue: 4, min: 0.1, max: 20, step: 0.1 },
      { id: "shareAppreciation", label: "Annual Share Growth (%)", type: "percentage", defaultValue: 6, min: 0, max: 30, step: 0.1 },
      { id: "years", label: "Compound Term (Years)", type: "number", defaultValue: 15, min: 1, max: 50, step: 1 }
    ],
    calculate: (inputs) => {
      const shares = inputs.initialShares || 500;
      const priceVal = inputs.sharePrice || 50;
      const yieldPct = (inputs.dividendYield || 4) / 100;
      const growthPct = (inputs.shareAppreciation || 6) / 100;
      const yrs = inputs.years || 15;

      // Plan A: Reinvestment (DRIP)
      let dripShares = shares;
      let dripPrice = priceVal;
      const chartData = [];

      // Plan B: Cash-out Dividends (No Reinvestment)
      let cashShares = shares;
      let cashPrice = priceVal;
      let accumulatedCashDividends = 0;

      for (let y = 1; y <= yrs; y++) {
        // DRIP Compounding
        dripPrice = dripPrice * (1 + growthPct);
        const dripPayout = dripShares * dripPrice * yieldPct;
        const newShares = dripPayout / dripPrice;
        dripShares += newShares;

        // Cash Out Plan
        cashPrice = cashPrice * (1 + growthPct);
        const cashPayout = cashShares * cashPrice * yieldPct;
        accumulatedCashDividends += cashPayout;

        if (y % 3 === 0 || y === yrs) {
          chartData.push({
            name: `Yr ${y}`,
            "DRIP Total Value": Math.round(dripShares * dripPrice),
            "Cash Out Total value": Math.round(cashShares * cashPrice + accumulatedCashDividends),
            "DRIP Share Count": Math.round(dripShares)
          });
        }
      }

      const dripFinalValue = dripShares * dripPrice;
      const cashFinalValue = cashShares * cashPrice + accumulatedCashDividends;
      const dripPremium = Math.max(0, dripFinalValue - cashFinalValue);

      return {
        outputs: [
          { label: "Portfolio Value (DRIP)", value: dripFinalValue, type: "currency", highlighted: true },
          { label: "Portfolio Value (Cash-Out Payout)", value: cashFinalValue, type: "currency" },
          { label: "Extra Wealth Gained via DRIP", value: dripPremium, type: "currency", highlighted: true },
          { label: "New Total Shares Accumulated", value: dripShares, type: "number" }
        ],
        chartData,
        recommendation: `Reinvesting dividends (DRIP) accumulates additional shares. Your share count grew from ${shares} to ${dripShares.toFixed(0)} over ${yrs} years, giving you $${dripPremium.toFixed(2)} in extra wealth compared to taking cash.`
      };
    },
    guide: {
      overview: "Calculates the massive benefits of automatically reinvesting dividend yields.",
      howToUse: "Key in starting shares count, share price, dividend yields, and capital growth projections.",
      formulaExplanation: "DRIP uses dividends to buy fractional shares recursively, capturing compound growth.",
      faq: [
        { question: "What is a DRIP?", answer: "Dividend Reinvestment Plan. Automatically reinvests cash distributions to buy more shares of the dividend-paying asset." },
        { question: "Are reinvested dividends taxed?", answer: "Yes, dividends are taxable in the year received, even if reinvested." }
      ],
      seoContent800Words: "Reinvesting dividend payouts generates exponential compounding curves on underlying holdings."
    }
  }
];
